import { useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import confetti from 'canvas-confetti'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { Avatar } from '@/components/design-system/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'

interface PostConnectionModalProps {
  open: boolean
  onClose: () => void
  worker: {
    id: string
    name: string
    avatar_url: string | null
  }
  context: string
  connectionOnly?: boolean
}

export default function PostConnectionModal({ open, onClose, worker, context, connectionOnly = false }: PostConnectionModalProps) {
  const avatarWrapRef = useRef<HTMLDivElement>(null)

  const handlePrimary = () => {
    router.visit(`/dashboard/digital_workers/${worker.id}`)
  }

  // Confetti burst saindo da posição do avatar quando o modal abre.
  useEffect(() => {
    if (!open) return

    // Respeita prefers-reduced-motion (baseline de acessibilidade).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let fireTimeout = 0
    let secondBurstTimeout = 0

    const fire = () => {
      const el = avatarWrapRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const origin = {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      }
      const colors = ['#22c55e', '#4ade80', '#86efac', '#bbf7d0', '#fafafa']
      confetti({ particleCount: 80, spread: 70, startVelocity: 35, origin, colors, scalar: 1 })
      secondBurstTimeout = window.setTimeout(() => {
        confetti({ particleCount: 40, spread: 100, startVelocity: 28, origin, colors, scalar: 0.9 })
      }, 150)
    }

    // Aguarda o zoom-in do Radix Dialog (duration-200) terminar antes de medir,
    // pra que o rect do avatar já esteja na posição final.
    fireTimeout = window.setTimeout(fire, 200)

    return () => {
      clearTimeout(fireTimeout)
      clearTimeout(secondBurstTimeout)
      // Interrompe partículas em voo se o modal fechar rápido.
      confetti.reset()
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-col items-center gap-4 pt-4 pb-2 text-center">
          <div ref={avatarWrapRef} className="relative">
            <div className="rounded-full ring-4 ring-green-500/20">
              <Avatar src={worker.avatar_url || undefined} name={worker.name} size="xl" />
            </div>
            <span
              aria-hidden="true"
              className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-background"
            >
              <CheckCircle2 className="h-7 w-7 text-green-500 fill-green-500/15" strokeWidth={2.25} />
            </span>
          </div>

          <div className="space-y-1.5">
            <DialogTitle className="text-xl font-semibold">{connectionOnly ? 'Conexão concluída' : 'Tudo pronto!'}</DialogTitle>
            <DialogDescription className="text-base">
              {connectionOnly ? (
                <>A conta de <span className="font-medium text-foreground">{context}</span> foi conectada a <span className="font-medium text-foreground">{worker.name}</span>.</>
              ) : (
                <><span className="font-medium text-foreground">{worker.name}</span>{' '}
                  agora tem acesso a <span className="font-medium text-foreground">{context}</span>.</>
              )}
            </DialogDescription>
          </div>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <Button onClick={handlePrimary} size="lg" className="w-full">
            Ver {worker.name}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button onClick={onClose} variant="outline" size="lg" className="w-full">
            Conectar outros colaboradores
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
